// src/pages/AddCheckInTimePage.tsx
import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container,
  Paper,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Grid,
  useTheme,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  AccessTime as AccessTimeIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

const AddCheckInTimePage: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const accentColor = '#00AEEF';
  const accentHover = '#0095cf';
  const paperBg = isDark
    ? 'linear-gradient(135deg, rgba(15, 23, 42, 0.96) 0%, rgba(15, 23, 42, 0.88) 100%)'
    : 'linear-gradient(135deg, rgba(255, 255, 255, 0.97) 0%, rgba(255, 255, 255, 0.92) 100%)';
  const paperBorder = isDark ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid rgba(0, 0, 0, 0.04)';
  const selectSx = {
    borderRadius: 3,
    backgroundColor: isDark ? 'rgba(15, 23, 42, 0.7)' : 'rgba(255, 255, 255, 0.9)',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
      backgroundColor: isDark ? 'rgba(15, 23, 42, 0.84)' : '#ffffff',
      boxShadow: isDark
        ? '0 8px 22px rgba(0, 0, 0, 0.45)'
        : '0 4px 12px rgba(0, 174, 239, 0.12)',
    },
    '&.Mui-focused': {
      backgroundColor: isDark ? 'rgba(15, 23, 42, 0.9)' : '#ffffff',
      boxShadow: isDark
        ? '0 12px 30px rgba(0, 174, 239, 0.3)'
        : '0 8px 20px rgba(0, 174, 239, 0.18)',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)',
      borderWidth: 1.5,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: accentColor,
      borderWidth: 2,
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: accentColor,
      borderWidth: 2.5,
    },
  };
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [checkInTime, setCheckInTime] = useState('');
  const [hour, setHour] = useState('');
  const [minute, setMinute] = useState('');

  const hotelId = searchParams.get('hotelId');
  const reservationId = searchParams.get('reservationId');
  const profileId = searchParams.get('profileId');
  const surname = searchParams.get('surname');

  // Common check-in times
  const quickTimes = [
    { label: '14:00', hour: '14', minute: '00', display: '14:00' },
    { label: '15:00', hour: '15', minute: '00', display: '15:00' },
    { label: '16:00', hour: '16', minute: '00', display: '16:00' },
    { label: '17:00', hour: '17', minute: '00', display: '17:00' },
    { label: '18:00', hour: '18', minute: '00', display: '18:00' },
    { label: '19:00', hour: '19', minute: '00', display: '19:00' },
  ];

  // Generate hours (0-23)
  const hours = Array.from({ length: 24 }, (_, i) => 
    i.toString().padStart(2, '0')
  );

  // Generate minutes (0, 15, 30, 45)
  const minutes = ['00', '15', '30', '45'];

  // Sync hour/minute ↔ checkInTime (intentionally partial deps to avoid loops)
  React.useEffect(() => {
    if (hour && minute) {
      setCheckInTime(`${hour}:${minute}`);
    } else if (checkInTime) {
      const [h, m] = checkInTime.split(':');
      setHour(h || '');
      setMinute(m || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hour, minute]);

  React.useEffect(() => {
    if (checkInTime && !hour && !minute) {
      const [h, m] = checkInTime.split(':');
      setHour(h || '');
      setMinute(m || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkInTime]);

  const handleQuickTimeSelect = (selectedHour: string, selectedMinute: string) => {
    setHour(selectedHour);
    setMinute(selectedMinute);
    setCheckInTime(`${selectedHour}:${selectedMinute}`);
  };

  const handleHourChange = (newHour: string) => {
    setHour(newHour);
    if (minute) {
      setCheckInTime(`${newHour}:${minute}`);
    }
  };

  const handleMinuteChange = (newMinute: string) => {
    setMinute(newMinute);
    if (hour) {
      setCheckInTime(`${hour}:${newMinute}`);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    if (!hotelId || !reservationId) {
      setError(t('onboarding.missingParams') || 'Missing required parameters');
      return;
    }

    if (!checkInTime || !hour || !minute) {
      setError(t('onboarding.timeRequired') || 'Please select a check-in time');
      return;
    }

    setIsLoading(true);

    try {
      // TODO: Save check-in time via API
      setSuccess(true);
      
      setTimeout(() => {
        const params = new URLSearchParams({
          hotelId: hotelId!,
          reservationId: reservationId!,
        });
        if (profileId) params.append('profileId', profileId);
        if (surname) params.append('surname', surname);
        navigate(`/guest-onboarding?${params.toString()}`);
      }, 1500);
    } catch (error: any) {
      console.error('Failed to save check-in time:', error);
      setError(error.response?.data?.message || t('onboarding.failedToSaveCheckIn') || 'Failed to save check-in time. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    const params = new URLSearchParams({
      hotelId: hotelId!,
      reservationId: reservationId!,
    });
    if (profileId) params.append('profileId', profileId);
    if (surname) params.append('surname', surname);
    navigate(`/guest-onboarding?${params.toString()}`);
  };

  // Format time for display
  const formatTime = (h: string, m: string) => {
    if (!h || !m) return '';
    const hourNum = parseInt(h, 10);
    const period = hourNum >= 12 ? 'PM' : 'AM';
    const displayHour = hourNum === 0 ? 12 : hourNum > 12 ? hourNum - 12 : hourNum;
    return `${displayHour}:${m} ${period}`;
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 8, mb: 4 }}>
      <Paper
        elevation={3}
        sx={{
          p: 4,
          borderRadius: 3,
          background: paperBg,
          backdropFilter: 'blur(18px)',
          border: paperBorder,
          boxShadow: isDark
            ? '0 25px 70px rgba(0, 0, 0, 0.6)'
            : '0 30px 80px rgba(15, 23, 42, 0.12)',
        }}
      >
          <Box sx={{ mb: 4 }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={handleBack}
              disabled={isLoading || success}
              sx={{
                mb: 2,
                textTransform: 'none',
                color: accentColor,
                fontWeight: 600,
                '&:hover': {
                  backgroundColor: isDark ? 'rgba(0, 174, 239, 0.15)' : 'rgba(0, 174, 239, 0.08)',
                },
              }}
            >
              {t('login.back')}
            </Button>
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Typography
                variant="h4"
                component="h1"
                gutterBottom
                sx={{ fontWeight: 700, color: accentColor }}
              >
                {t('onboarding.addCheckInTime') || 'Estimated Check-in Time'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('onboarding.addCheckInTimeDesc') || 'Would you like to specify your estimated arrival time?'}
              </Typography>
            </Box>
          </Box>

          {error && (
            <Alert 
              severity="error" 
              sx={{ 
                mb: 3,
                borderRadius: 2.5,
                boxShadow: '0 2px 8px rgba(220, 38, 38, 0.15)',
                animation: 'slideDown 0.3s ease-out',
                '@keyframes slideDown': {
                  from: {
                    opacity: 0,
                    transform: 'translateY(-10px)',
                  },
                  to: {
                    opacity: 1,
                    transform: 'translateY(0)',
                  },
                },
              }}
            >
              {error}
            </Alert>
          )}

          {success && (
            <Alert 
              severity="success" 
              sx={{ 
                mb: 3,
                borderRadius: 2.5,
                boxShadow: '0 2px 8px rgba(34, 197, 94, 0.15)',
              }}
            >
              {t('onboarding.checkInTimeSaved') || 'Your check-in time has been successfully saved!'}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
            {/* Quick Time Selection */}
            <Box sx={{ mb: 4 }}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 700,
                  color: '#1f2937',
                  mb: 2,
                  fontSize: '0.95rem',
                }}
              >
                {t('onboarding.quickSelect') || 'Quick Select'}
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1.5,
                }}
              >
                {quickTimes.map((time) => {
                  const isSelected = hour === time.hour && minute === time.minute;
                  return (
                    <Chip
                      key={time.label}
                      label={time.display}
                      onClick={() => handleQuickTimeSelect(time.hour, time.minute)}
                      disabled={isLoading || success}
                      sx={{
                        height: 44,
                        fontSize: '0.95rem',
                        fontWeight: 600,
                        cursor: 'pointer',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        backgroundColor: isSelected
                          ? accentColor
                          : (isDark ? 'rgba(15, 23, 42, 0.7)' : 'rgba(255, 255, 255, 0.9)'),
                        color: isSelected ? '#ffffff' : (isDark ? '#e2e8f0' : '#374151'),
                        border: isSelected
                          ? `2px solid ${accentColor}`
                          : `2px solid ${isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)'}`,
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: '0 6px 18px rgba(0, 174, 239, 0.25)',
                          backgroundColor: isSelected
                            ? accentHover
                            : (isDark ? 'rgba(0, 174, 239, 0.15)' : 'rgba(0, 174, 239, 0.08)'),
                        },
                        '&:disabled': {
                          opacity: 0.6,
                          cursor: 'not-allowed',
                        },
                      }}
                    />
                  );
                })}
              </Box>
            </Box>

            {/* Custom Time Selection */}
            <Box sx={{ mb: 4 }}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 700,
                  color: '#1f2937',
                  mb: 2,
                  fontSize: '0.95rem',
                }}
              >
                {t('onboarding.customTime') || 'Custom Time'}
              </Typography>
              
              {/* Time Display */}
              {hour && minute && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 3,
                    p: 3,
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, #00AEEF 0%, #0087c1 100%)',
                    color: '#ffffff',
                    boxShadow: '0 12px 32px rgba(0, 174, 239, 0.45)',
                  }}
                >
                  <AccessTimeIcon sx={{ fontSize: '2rem', mr: 2 }} />
                  <Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 800,
                        lineHeight: 1,
                        mb: 0.5,
                      }}
                    >
                      {hour}:{minute}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        opacity: 0.9,
                        fontWeight: 500,
                      }}
                    >
                      {formatTime(hour, minute)}
                    </Typography>
                  </Box>
                </Box>
              )}

              {/* Hour and Minute Selectors */}
              <Grid container spacing={2}>
                <Grid size={{ xs: 6 }}>
                  <FormControl fullWidth>
                    <InputLabel>{t('onboarding.hour') || 'Hour'}</InputLabel>
                    <Select
                      value={hour}
                      label={t('onboarding.hour') || 'Hour'}
                      onChange={(e) => handleHourChange(e.target.value)}
                      disabled={isLoading || success}
                      sx={selectSx}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            borderRadius: 3,
                            mt: 1,
                            boxShadow: isDark ? '0 25px 60px rgba(0, 0, 0, 0.55)' : '0 8px 32px rgba(0, 0, 0, 0.12)',
                            backgroundColor: isDark ? 'rgba(15, 23, 42, 0.98)' : '#ffffff',
                            maxHeight: 300,
                            '& .MuiMenuItem-root': {
                              borderRadius: 2,
                              mx: 1,
                              my: 0.5,
                              '&:hover': {
                                backgroundColor: isDark ? 'rgba(0, 174, 239, 0.15)' : 'rgba(0, 174, 239, 0.08)',
                              },
                              '&.Mui-selected': {
                                backgroundColor: accentColor,
                                color: '#ffffff',
                                '&:hover': {
                                  backgroundColor: accentHover,
                                },
                              },
                            },
                          },
                        },
                      }}
                    >
                      {hours.map((h) => (
                        <MenuItem key={h} value={h}>
                          {h}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <FormControl fullWidth>
                    <InputLabel>{t('onboarding.minute') || 'Minute'}</InputLabel>
                    <Select
                      value={minute}
                      label={t('onboarding.minute') || 'Minute'}
                      onChange={(e) => handleMinuteChange(e.target.value)}
                      disabled={isLoading || success}
                      sx={selectSx}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            borderRadius: 3,
                            mt: 1,
                            boxShadow: isDark ? '0 25px 60px rgba(0, 0, 0, 0.55)' : '0 8px 32px rgba(0, 0, 0, 0.12)',
                            backgroundColor: isDark ? 'rgba(15, 23, 42, 0.98)' : '#ffffff',
                            '& .MuiMenuItem-root': {
                              borderRadius: 2,
                              mx: 1,
                              my: 0.5,
                              '&:hover': {
                                backgroundColor: isDark ? 'rgba(0, 174, 239, 0.15)' : 'rgba(0, 174, 239, 0.08)',
                              },
                              '&.Mui-selected': {
                                backgroundColor: accentColor,
                                color: '#ffffff',
                                '&:hover': {
                                  backgroundColor: accentHover,
                                },
                              },
                            },
                          },
                        },
                      }}
                    >
                      {minutes.map((m) => (
                        <MenuItem key={m} value={m}>
                          {m}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                onClick={handleBack}
                disabled={isLoading || success}
                sx={{
                  px: 4,
                  py: 1.5,
                  borderRadius: 3,
                  borderColor: 'rgba(0, 0, 0, 0.12)',
                  borderWidth: 1.5,
                  color: '#6b7280',
                  textTransform: 'none',
                  fontSize: '1rem',
                  fontWeight: 600,
                  backgroundColor: 'rgba(255, 255, 255, 0.6)',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    borderColor: '#9ca3af',
                    borderWidth: 2,
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    transform: 'translateY(-1px)',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                  },
                }}
              >
                {t('login.back')}
              </Button>
              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={isLoading || success || !checkInTime}
                sx={{
                  px: 4,
                  py: 1.5,
                  backgroundColor: accentColor,
                  boxShadow: '0 18px 35px rgba(0, 174, 239, 0.35)',
                  '&:hover': {
                    backgroundColor: accentHover,
                    boxShadow: '0 18px 40px rgba(0, 174, 239, 0.45)',
                  },
                  '&:disabled': {
                    backgroundColor: isDark ? 'rgba(255, 255, 255, 0.12)' : '#cbd5e1',
                    boxShadow: 'none',
                    color: isDark ? 'rgba(255, 255, 255, 0.6)' : '#94a3b8',
                  },
                }}
              >
                {isLoading ? <CircularProgress size={24} color="inherit" /> : (t('onboarding.save') || 'Save')}
              </Button>
            </Box>
          </Box>
        </Paper>
      </Container>
  );
};

export default AddCheckInTimePage;
